import { useState, useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Trash2, Pencil, Plus, AlertCircle, BarChart4 } from "lucide-react";
import { Dialog } from "@/components/ui/dialog";
import { useData } from "@/hooks/useData";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatNumber } from "@/utils/numberFormatter";
import { MaterialModalForm } from "./MaterialModalForm";
import { Badge } from "@/components/ui/badge";
import { MaterialEntry, MaterialPayload, MaterialDoc, MaterialDocWithId } from "@/types/material";
import {
  calculateMaterialCarbonFootprint,
  calculateMaterialHydricFootprint,
  calculateTotalMaterialCarbonFootprint,
  calculateTotalMaterialHydricFootprint,
} from "../../utils/calculations/annualConsumptions";
import { convertDbUnitToUIUnit } from "@/components/footprints/utils/calculations/conversions";

// Empty material entry for new entries
const emptyEntry: MaterialEntry = {
  materialType: "",
  quantity: "",
  unit: "Unidade",
  pc_mat: 0,
  ph_mat: 0,
};

interface MaterialStepProps {
  existingData?: MaterialPayload;
  onDataChange: (payload: MaterialPayload) => void;
  showTitle?: boolean;
}

export const MaterialsStep = ({ existingData, onDataChange, showTitle = true }: MaterialStepProps) => {
  const { t } = useLanguage();

  // Initialize entries array from existingData.entries, if provided
  const [materialEntries, setMaterialEntries] = useState<MaterialEntry[]>(
    Array.isArray(existingData?.entries) ? existingData.entries : []
  );
  const [modalOpen, setModalOpen] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [formEntry, setFormEntry] = useState<MaterialEntry>(emptyEntry);
  const [unitOptions, setUnitOptions] = useState<string[]>([]);

  // Fetch material docs
  const { data: materialDocs } = useData<MaterialDocWithId>({
    collectionName: "setup_materials",
  });

  // Get material options not already selected (except when editing)
  const materialOptions = (materialDocs || [])
    .map((doc) => ({ value: doc.name, label: doc.name }))
    .sort((a, b) => a.label.localeCompare(b.label))
    .filter((opt) => {
      // Only filter if multiple reporting units are defined in setup
      if (materialDocs && materialDocs.length > 1) {
        return !materialEntries.some((e, index) =>
          e.materialType === opt.value && index !== editingIndex
        );
      }
      return true;
    });

  // Update unit options when picking a materialType
  useEffect(() => {
    if (formEntry.materialType && materialDocs) {
      const selected = materialDocs.find((doc) => doc.name === formEntry.materialType);
      if (selected?.units && selected.units.length > 0) {
        // Convert database units to UI units
        const uiUnits = selected.units.map((dbUnit) => convertDbUnitToUIUnit(dbUnit));
        setUnitOptions(uiUnits);
      } else {
        setUnitOptions(["Unidade"]);
      }
    } else {
      setUnitOptions([]);
    }
  }, [formEntry.materialType, materialDocs]);

  // Sync up if existingData changes
  useEffect(() => {
    const entries = Array.isArray(existingData?.entries) ? existingData.entries : [];
    setMaterialEntries(entries);
  }, [existingData]);

  /** Open form modal (add vs edit) */
  const openModal = (entry?: MaterialEntry, idx?: number) => {
    if (entry && typeof idx === "number") {
      setFormEntry({ ...entry });
      setEditingIndex(idx);
    } else {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => {
      setFormEntry(emptyEntry);
      setEditingIndex(null);
      setUnitOptions([]);
    }, 100);
  };

  /** Find material document by name */
  const getMaterialDoc = (materialType: string): MaterialDoc | undefined => {
    return (materialDocs || []).find((doc) => doc.name === materialType);
  };

  // Recalculate footprints when materialDocs are loaded or changed
  // CRITICAL: Only recalculate if entries don't already have valid footprint values
  // This prevents overwriting valid values on initial load after login
  useEffect(() => {
    if (materialDocs && materialDocs.length > 0 && materialEntries.length > 0) {
      let hasChanges = false;

      const updatedEntries = materialEntries.map(entry => {
        // Skip entries without quantity
        if (!entry.quantity || Number(entry.quantity) === 0) {
          return entry;
        }

        const materialDoc = getMaterialDoc(entry.materialType);
        if (!materialDoc) {
          return entry;
        }

        // CRITICAL FIX: Check if entry already has valid footprint values
        // Only recalculate if values are missing (undefined, null, or 0 for entries with quantity > 0)
        const hasExistingFootprints = 
          (entry.pc_mat !== undefined && entry.pc_mat !== null && entry.pc_mat > 0) ||
          (entry.ph_mat !== undefined && entry.ph_mat !== null && entry.ph_mat > 0);
        
        // If entry has valid footprints, only update factors if they changed, but preserve footprint values
        if (hasExistingFootprints) {
          const carbonResult = calculateMaterialCarbonFootprint(entry, materialDoc);
          const hydricResult = calculateMaterialHydricFootprint(entry, materialDoc);
          
          // Only update if factors changed, but preserve existing footprint values
          if (
            entry.carbonFactor !== carbonResult.factor ||
            entry.hydricFactor !== hydricResult.factor
          ) {
            hasChanges = true;
            return {
              ...entry,
              carbonFactor: carbonResult.factor,
              hydricFactor: hydricResult.factor,
              // Preserve existing footprint values - don't recalculate
              pc_mat: entry.pc_mat,
              ph_mat: entry.ph_mat,
            };
          }
          return entry;
        }

        // Only recalculate if entry doesn't have valid footprints yet (new entries or entries with missing values)
        const carbonResult = calculateMaterialCarbonFootprint(entry, materialDoc);
        const hydricResult = calculateMaterialHydricFootprint(entry, materialDoc);

        // Preserve existing values if recalculation produces 0 and we had a non-zero value
        // This prevents corruption when recalculation fails or returns 0 due to missing data
        const newPcMat = (carbonResult.footprint === 0 && entry.pc_mat > 0) 
          ? entry.pc_mat 
          : carbonResult.footprint;
        const newPhMat = (hydricResult.footprint === 0 && entry.ph_mat > 0) 
          ? entry.ph_mat 
          : hydricResult.footprint;

        // Check if values changed
        if (
          entry.pc_mat !== newPcMat ||
          entry.ph_mat !== newPhMat ||
          entry.carbonFactor !== carbonResult.factor ||
          entry.hydricFactor !== hydricResult.factor
        ) {
          hasChanges = true;
          return {
            ...entry,
            pc_mat: newPcMat,
            ph_mat: newPhMat,
            carbonFactor: carbonResult.factor,
            hydricFactor: hydricResult.factor,
          };
        }

        return entry;
      });

      // Only update if something actually changed
      if (hasChanges) {
        setMaterialEntries(updatedEntries);
        onDataChange({
          entries: updatedEntries,
          total_pc_mat: calculateTotalMaterialCarbonFootprint(updatedEntries),
          total_ph_mat: calculateTotalMaterialHydricFootprint(updatedEntries),
        });
      }
    }
  }, [materialDocs, materialEntries, getMaterialDoc, onDataChange]);

  /** Save (add/edit) */
  const handleSave = () => {
    const materialDoc = getMaterialDoc(formEntry.materialType);

    // Calculate footprints and get factors used
    const carbonResult = calculateMaterialCarbonFootprint(formEntry, materialDoc);
    const hydricResult = calculateMaterialHydricFootprint(formEntry, materialDoc);

    const entryWithFootprints: MaterialEntry = {
      ...formEntry,
      pc_mat: carbonResult.footprint,
      ph_mat: hydricResult.footprint,
      carbonFactor: carbonResult.factor,
      hydricFactor: hydricResult.factor,
      materialName: formEntry.materialType,
      setup_item_id: materialDoc?.id || null,
      setup_item_name: formEntry.materialType || "",
    };

    const newEntries = [...materialEntries];
    if (editingIndex !== null) newEntries[editingIndex] = entryWithFootprints;
    else newEntries.push(entryWithFootprints);

    setMaterialEntries(newEntries);
    const newPayload = {
      entries: newEntries,
      total_pc_mat: calculateTotalMaterialCarbonFootprint(newEntries),
      total_ph_mat: calculateTotalMaterialHydricFootprint(newEntries),
    };

    onDataChange(newPayload);
    closeModal();
  };

  /** Remove one entry */
  const handleRemoveEntry = (index: number) => {
    const newEntries = materialEntries.filter((_, i) => i !== index);
    setMaterialEntries(newEntries);
    onDataChange({
      entries: newEntries,
      total_pc_mat: calculateTotalMaterialCarbonFootprint(newEntries),
      total_ph_mat: calculateTotalMaterialHydricFootprint(newEntries),
    });
  };

  const handleFormEntryChange = (entry: MaterialEntry) => {
    // If material type changed, update unit to the first available unit for this material
    if (entry.materialType !== formEntry.materialType) {
      const selectedMaterial = materialDocs?.find((doc) => doc.name === entry.materialType);
      if (selectedMaterial?.units && selectedMaterial.units.length > 0) {
        // Convert database unit to UI unit
        entry.unit = convertDbUnitToUIUnit(selectedMaterial.units[0]);
      } else {
        // Only use Unidade as a fallback if no units are defined
        entry.unit = "Unidade";
      }
    }

    setFormEntry(entry);
  };

  return (
    <div className="space-y-6">
      {/* Title + Add button */}
      {showTitle ? (
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">{t("footprints.categories.materials.title")}</h2>
            <p className="text-sm text-gray-500 mt-1">{t("footprints.guidelines.materials")}</p>
          </div>
          {materialOptions.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-700 text-sm mb-4">
              {t("materials.noOptionsAvailable")}
            </div>
          ) : (
            <Button
              onClick={() => openModal()}
              variant="outline"
              className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white"
              disabled={materialOptions.length === 0}
            >
              <Plus className="h-4 w-4" />
              {t("common.add")}
            </Button>
          )}
        </div>
      ) : (
        <div className="flex justify-end mb-4">
          {materialOptions.length === 0 ? (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-4 text-blue-700 text-sm mb-4">
              {t("materials.noOptionsAvailable")}
            </div>
          ) : (
            <Button
              onClick={() => openModal()}
              variant="outline"
              className="gap-2 bg-teal-600 text-white hover:bg-teal-700 hover:text-white"
              disabled={materialOptions.length === 0}
            >
              <Plus className="h-4 w-4" />
              {t("common.add")}
            </Button>
          )}
        </div>
      )}

      {/* No entries / list of cards */}
      {materialEntries.length === 0 ? (
        <Alert variant="default" className="bg-gray-50 border-gray-200">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-gray-400 mr-2" />
            <AlertDescription className="text-gray-500">{t("materials.noEntries")}</AlertDescription>
          </div>
        </Alert>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {materialEntries.map((entry, idx) => (
              <Card key={idx} className="flex flex-col h-full shadow-sm hover:shadow transition-shadow">
                <CardHeader className="bg-gray-50 pb-3">
                  <div className="flex justify-between items-center">
                    <CardTitle className="text-lg font-semibold">
                      {entry.setup_item_name || entry.materialName || entry.materialType || "-"}
                    </CardTitle>
                    <div className="flex space-x-1">
                      <Button size="sm" variant="ghost" onClick={() => openModal(entry, idx)}>
                        <Pencil className="h-4 w-4 text-gray-500 hover:text-teal-600" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => handleRemoveEntry(idx)}>
                        <Trash2 className="h-4 w-4 text-gray-500 hover:text-red-600" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="py-4">
                  <div className="space-y-2">
                    <div className="flex justify-between items-center mb-0 pb-4">
                      <span className="text-sm text-gray-500">{t("common.quantity")}:</span>
                      <Badge
                        variant="secondary"
                        className="bg-gray-50 text-gray-700 hover:bg-gray-50 hover:text-gray-700"
                      >
                        <span>
                          {formatNumber(Number(entry.quantity))} {entry.unit}
                        </span>
                      </Badge>
                    </div>
                  </div>
                </CardContent>

                <div className="mt-auto">
                  <div className="bg-teal-50/80 border-t py-3 px-4">
                    <div className="flex justify-between items-center text-teal-700 w-full">
                      <div className="flex items-center gap-1.5">
                        <BarChart4 className="h-5 w-5 align-middle" />
                        <span className="align-middle">{t("common.carbonFootprint")}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {formatNumber(entry.pc_mat)}{" "}
                        <span className="text-sm font-normal">
                          {t("dashboard.company.kgCO2")}
                        </span>
                      </span>
                    </div>
                  </div>

                  <div className="bg-cyan-50/50 border-t py-3 px-4">
                    <div className="flex justify-between items-center text-cyan-600 w-full">
                      <div className="flex items-center gap-1.5">
                        <BarChart4 className="h-5 w-5 align-middle" />
                        <span className="align-middle">{t("common.hydricFootprint")}</span>
                      </div>
                      <span className="text-lg font-bold">
                        {formatNumber(entry.ph_mat)}{" "}
                        <span className="text-sm font-normal">
                          m<sup>3</sup>
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-lg border border-teal-100 bg-white p-6">
              <div className="flex items-center gap-2 text-teal-700 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">{t("common.totalCarbonFootprint")}</span>
              </div>

              <p className="text-xs italic text-teal-600 ml-7 mb-4">
                {t("footprints.carbonFootprintMaterialsFormula")}
              </p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-teal-700">
                  {formatNumber(calculateTotalMaterialCarbonFootprint(materialEntries))}{" "}
                  <span>
                    {t("dashboard.company.kgCO2")}
                  </span>
                </span>
              </div>
            </div>

            <div className="rounded-lg border border-cyan-100 bg-white p-6">
              <div className="flex items-center gap-2 text-cyan-600 mb-1">
                <BarChart4 className="h-5 w-5" />
                <span className="text-lg font-medium">{t("common.totalHydricFootprint")}</span>
              </div>

              <p className="text-xs italic text-cyan-600 ml-7 mb-4">
                {t("footprints.hydricFootprintMaterialsFormula")}
              </p>

              <div className="flex justify-end">
                <span className="text-xl font-bold text-cyan-600">
                  {formatNumber(calculateTotalMaterialHydricFootprint(materialEntries))}{" "}
                  <span>
                    m<sup>3</sup>
                  </span>
                </span>
              </div>
            </div>
          </div>
        </>
      )}

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <MaterialModalForm
          onClose={closeModal}
          onSave={handleSave}
          formEntry={formEntry}
          onFormEntryChange={handleFormEntryChange}
          materialOptions={materialOptions}
          unitOptions={unitOptions}
          editing={editingIndex !== null}
        />
      </Dialog>
    </div>
  );
};

export type { MaterialPayload };
